import { Alert, ScrollArea, Skeleton } from '@ibsys/kit';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useKmDictionaries, useKmEntitiesInfinite, useSearchKmEntitiesInfinite } from '@/entities/ontology';
import { useGuardedInfiniteScroll } from '@/shared/hooks';
import { InfiniteScrollSentinel } from '@/shared/ui';
import { KmEntityListItem } from './KmEntityListItem';

type Props = {
  /** Коды доменов из отбора */
  domain: string[];
  /** Коды типов сущностей из отбора */
  type: string[];
  /** Идентификаторы сущностей, связь с которыми отбирает выдачу */
  related: number[];
  /** Строка поиска по значениям атрибутов: поле отдаёт её по Enter или уходу фокуса */
  search: string;
  /** Идентификатор открытой сущности */
  selectedId?: string;
  onSelect: (id: number) => void;
};

/**
 * Левая панель: сущности модели знаний под текущим отбором, с догрузкой окнами.
 *
 * Пока строка поиска пуста, выдачу даёт список с фильтрами; с непустой строкой — полнотекстовый
 * поиск по значениям атрибутов с теми же фильтрами.
 */
export const KmEntityList: FC<Props> = ({ domain, type, related, search, selectedId, onSelect }) => {
  const { t } = useTranslation(['kmEntities']);

  const query = search.trim();
  const isSearchMode = Boolean(query);

  const listQuery = useKmEntitiesInfinite({ domain, type, related, includeDeleted: false }, { enabled: !isSearchMode });
  const searchQuery = useSearchKmEntitiesInfinite({ query, domain, type, related });

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = isSearchMode ? searchQuery : listQuery;

  // Строки списка несут только коды: названия типа и домена берём из общего справочника
  const { getEntityTypeName, getDomainName } = useKmDictionaries();

  const viewportRef = useRef<HTMLDivElement>(null);
  const { sentinelRef, isAutoLoadStopped, resumeAutoLoad } = useGuardedInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    pagesCount: data?.pages.length ?? 0,
    // Смена отбора начинает список заново, поэтому возобновляет и остановленную догрузку
    resetKey: `${query}|${domain.join(',')}|${type.join(',')}|${related.join(',')}`,
    threshold: 500,
    root: viewportRef,
  });

  const entities = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 h-full">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  if (!entities.length) {
    return <Alert type="info" className="text-muted-foreground p-4" title={t('kmEntities:listIsEmpty')} />;
  }

  return (
    <ScrollArea viewPortProps={{ ref: viewportRef }}>
      <ul className="flex flex-col gap-4">
        {entities.map((entity) => (
          <KmEntityListItem
            key={entity.id}
            entity={entity}
            typeName={getEntityTypeName(entity.entityTypeCode)}
            domainName={entity.domainCode ? getDomainName(entity.domainCode) : undefined}
            isActive={selectedId === String(entity.id)}
            onSelect={onSelect}
          />
        ))}
      </ul>
      <InfiniteScrollSentinel
        sentinelRef={sentinelRef}
        isFetchingNextPage={isFetchingNextPage}
        isAutoLoadStopped={isAutoLoadStopped}
        onRetry={resumeAutoLoad}
      />
    </ScrollArea>
  );
};
